import type ReleaseFlag from 'api/legacy/releaseFlags/enumReleaseFlags';
import useReleaseFlags from 'api/legacy/releaseFlags/useOrganizationReleaseFlags';
import usePublicReleaseFlags from 'api/legacy/releaseFlags/usePublicReleaseFlags';
import { useMemo } from 'react';
import useAuth from 'state/contexts/AuthContext/useAuth.ts';
import useOrganization from 'state/contexts/Organization/useOrganization';
import type { LegacyOrganization, LegacyUser, OrganizationRole } from 'types/legacyDashboard';
import EnumPermissions from './enumPermissions';
import {
  labelForRole,
  type OrganizationRoleLabel,
  translatePermissionsToRole,
} from './permissionsTools';

interface UseUserInfoReturn extends Partial<LegacyUser> {
  isSuperAdmin: boolean;
  selectedOrganization?: LegacyOrganization;
  organizationRole: OrganizationRole;
  organizationRoleLabel: OrganizationRoleLabel;
  permissions?: Array<EnumPermissions>;
  invalidOrg?: boolean;
  invalidate: () => void;
  releaseFlags: Set<ReleaseFlag>;
}

/**
 * @description Requires AuthContext and OrganizationContext to be provided
 */
const useUserInfo = (): UseUserInfoReturn => {
  const { isAuthenticated, organizations, user, loadingUserDetails, invalidate } = useAuth();

  // TODO: This is pulling the organization from CONTEXT which may or may not be the selected and logged in organization. Need to delineate between the two, especially for SIM details
  const orgId = useOrganization();

  const selectedOrganization = useMemo(
    () => organizations.find((org) => org.id === orgId),
    [orgId, organizations]
  );

  const orgUserInfo = useMemo(
    () => selectedOrganization?.users.find((orgUser) => orgUser.id === user.id),
    [selectedOrganization, user.id]
  );

  const organizationRole = translatePermissionsToRole(orgUserInfo?.permissions);
  const organizationRoleLabel = useMemo(() => labelForRole(organizationRole), [organizationRole]);

  const {
    publicReleaseFlags,
    error: publicReleaseFlagsError,
    isLoading: loadingPublicReleaseFlags,
  } = usePublicReleaseFlags(isAuthenticated);

  const {
    organizationReleaseFlags,
    error: releaseFlagsError,
    isLoading: loadingReleaseFlags,
  } = useReleaseFlags(isAuthenticated ? orgId : undefined);

  const releaseFlags = useMemo(
    () => new Set([...publicReleaseFlags, ...organizationReleaseFlags]),
    [organizationReleaseFlags, publicReleaseFlags]
  );

  const loading = loadingUserDetails || loadingPublicReleaseFlags || loadingReleaseFlags;
  const error = publicReleaseFlagsError || releaseFlagsError;

  return useMemo(
    () =>
      !isAuthenticated || loading || error
        ? {
            organizationRole,
            organizationRoleLabel,
            invalidate,
            releaseFlags: new Set(),
            isSuperAdmin: false,
          }
        : {
            ...user,
            isSuperAdmin: ['adm', 'hdm'].includes(user.role ?? ''),
            selectedOrganization,
            organizationRole,
            organizationRoleLabel,
            permissions: orgUserInfo?.permissions ?? ([] as Array<EnumPermissions>),
            invalidOrg: !orgUserInfo,
            invalidate,
            releaseFlags,
          },
    [
      isAuthenticated,
      loading,
      error,
      organizationRole,
      organizationRoleLabel,
      invalidate,
      user,
      selectedOrganization,
      orgUserInfo,
      releaseFlags,
    ]
  );
};

export default useUserInfo;
